import {
  Checkbox,
  FormGroup,
  InputGroup,
  NonIdealState,
  Spinner,
} from "@blueprintjs/core";
import { debounce } from "lodash";
import {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ITEM_GRID_QUERY_SEARCH_DEBOUNCE_MS } from "../../constants/constants";
import { ItemGridFilter, type ItemGridFilterProps } from "./ItemGridFilter";

export type FilterValueEntry = {
  id: string;
  name: string;
  checked: boolean;
};

function ItemGridFilterDataListContentShell({
  error,
  items,
  loading,
  children,
}: PropsWithChildren<{
  error?: Error;
  items?: FilterValueEntry[];
  loading?: boolean;
}>) {
  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <NonIdealState
        icon="warning-sign"
        title="Error"
        description={error.message}
      />
    );
  }

  if (!items?.length) {
    return <NonIdealState title="No items" />;
  }

  return <>{children}</>;
}

type ItemGridFilterDataListProps = Omit<
  ItemGridFilterProps,
  "onClickFilterReset" | "children"
> & {
  error?: Error;
  items?: FilterValueEntry[];
  loading?: boolean;
  onQueryValueChanged: (query: string) => void;
  onSelectAllChanged: (checked: boolean) => void;
  onRequestFilterReset: () => void;
  onRequestFilterUpdate: (id: string, include: boolean) => void;
};

export function ItemGridFilterDataList({
  error,
  items,
  loading,
  property,
  text,
  onQueryValueChanged,
  onSelectAllChanged,
  onRequestFilterReset,
  onRequestFilterUpdate,
}: ItemGridFilterDataListProps) {
  const [queryValueLocal, setQueryValueLocal] = useState<string>("");

  const debouncedUpdateQuery = useRef(
    debounce((query: string) => {
      onQueryValueChanged(query);
    }, ITEM_GRID_QUERY_SEARCH_DEBOUNCE_MS),
  );

  const indeterminate = useMemo(
    () =>
      (items?.some((item) => item.checked) &&
        !items?.every((item) => item.checked)) ||
      false,
    [items],
  );

  useEffect(
    function effectOnLocalQueryValueChanged() {
      debouncedUpdateQuery.current(queryValueLocal);
    },
    [debouncedUpdateQuery, queryValueLocal],
  );

  return (
    <ItemGridFilter
      property={property}
      onClickFilterReset={() => onRequestFilterReset()}
      text={text}
    >
      <FormGroup>
        <InputGroup
          type="text"
          placeholder="Search"
          value={queryValueLocal}
          onChange={(e) => setQueryValueLocal(e.target.value)}
        />
      </FormGroup>
      <div className="flex flex-col grow w-full min-h-0 overflow-auto">
        <ItemGridFilterDataListContentShell
          error={error}
          items={items}
          loading={loading}
        >
          <Checkbox
            checked={items?.every((item) => item.checked)}
            className="text-ellipsis whitespace-nowrap outline-none"
            indeterminate={indeterminate}
            label="Select all"
            title="Select all"
            onChange={() => {
              onSelectAllChanged(
                indeterminate
                  ? true
                  : items?.every((item) => item.checked)
                    ? false
                    : true,
              );
            }}
          />
          {items?.map((item, index) => (
            <Checkbox
              key={index}
              checked={item.checked}
              className="text-ellipsis whitespace-nowrap outline-none"
              label={item.name}
              title={item.name}
              onChange={() => {
                onRequestFilterUpdate(item.id, !item.checked);
              }}
            />
          ))}
        </ItemGridFilterDataListContentShell>
      </div>
    </ItemGridFilter>
  );
}
